import { styled } from "goober";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { StatusBadgeColor } from "./StatusBadge";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";
import { ClickButton } from "./ClickButton";
import { Icon, type IconName } from "./Icon";
import { Link } from "./Link";
import { PressButton } from "./PressButton";
import { RefreshSpin } from "./RefreshSpin";
import { Text } from "./Text";
import {
  Tooltip,
  type Align,
  getLastTooltipCloseTime,
  getPositionStyles,
  setLastTooltipCloseTime,
  TOOLTIP_DELAY,
  type Vertical,
  WARM_PERIOD,
} from "./Tooltip";

const Container = styled("div")`
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
`;

const Badge = styled("div")<{ "data-collapse-at": number }>`
  --status-pill-compact: 0;
  --status-pill-action-display: inline-flex;
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  height: 20px;
  border-radius: 38px;

  &[aria-busy="true"] [data-status-trigger]:disabled {
    opacity: 1;
  }

  .hover-shape {
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: inherit;
    background: inherit;
    pointer-events: none;
    transition:
      left 75ms ease-in-out,
      right 75ms ease-in-out;
  }

  &:has([data-status-trigger]:hover),
  &:has([data-status-trigger]:active),
  &:has([data-status-trigger]:focus-visible) {
    .hover-shape {
      background: var(--status-pill-hover-bg);
    }
    [data-status-action] {
      color: var(--status-pill-hover-fg);
    }
  }

  &:has([data-status-action]:hover),
  &:has([data-status-action]:active),
  &:has([data-status-action]:focus-visible) {
    .hover-shape {
      left: calc(100% - 20px);
      background: var(--status-pill-hover-bg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .hover-shape {
      transition: none;
    }
  }

  @container status-pill (width < ${(props) => props["data-collapse-at"]}px) {
    --status-pill-compact: 1;
    --status-pill-action-display: none;
  }
  [data-status-label] {
    display: flex;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  [data-compact-icon] {
    display: none;
  }
  @container status-pill (width < 40px) {
    &[data-has-compact-icon="true"] {
      [data-status-label] {
        display: none;
      }
      [data-compact-icon] {
        display: inline-flex;
      }
    }
  }
`;

const Pill = styled("span")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 20px;
  max-width: 100%;
  height: 20px;
  padding: 0 8px;
  white-space: nowrap;
`;

const InlineAction = styled("span")`
  display: var(--status-pill-action-display);
  flex-shrink: 0;
`;

const InlineActionButton = styled(ClickButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  &:hover {
    color: var(--status-pill-hover-fg);
  }
`;

const Trigger = styled(PressButton)`
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  border-radius: 38px;
  &:hover {
    color: var(--status-pill-hover-fg);
  }
`;

const TriggerLink = styled(Link)`
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  border-radius: 38px;
  color: inherit;
  text-decoration: none;
  &:hover {
    color: var(--status-pill-hover-fg);
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const Popover = styled(AnchoredPopover)`
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  box-shadow: var(--elevation-low);
  padding: 6px 8px;
  z-index: ${zIndexes.tooltip};
  width: max-content;
  max-width: min(300px, calc(100vw - 24px));
  overflow-wrap: anywhere;
`;

const TooltipBody = styled("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 10px;
  line-height: 14px;
  cursor: default;
`;

const TooltipMessage = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TooltipActionButton = styled(ClickButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 4px;
  border-radius: 9999px;
  color: var(--tooltip-fg-subtle);
  margin-left: auto;
  transition:
    color 150ms ease,
    background-color 100ms ease;
  &:hover,
  &:focus-visible {
    color: var(--tooltip-fg);
    background: var(--tooltip-highlight);
  }
  &:focus-visible {
    outline-color: var(--tooltip-fg);
  }
`;

export const StatusPillCell = styled("div")`
  container: status-pill / inline-size;
  width: 100%;
  min-width: 0;
`;

type Action = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

type Props = {
  color: StatusBadgeColor;
  label: string;
  detail: ReactNode;
  running?: boolean;
  action?: Action;
  detailsUrl?: string;
  onPress?: () => void;
  triggerLabel?: string;
  disabled?: boolean;
  passive?: boolean;
  inheritLink?: boolean;
  compactAt?: number;
  compactIcon?: IconName;
};

export function StatusPill({
  color,
  label,
  detail,
  running = false,
  action,
  detailsUrl,
  onPress,
  triggerLabel = label,
  disabled = false,
  passive = false,
  inheritLink = false,
  compactAt = 80,
  compactIcon,
}: Props) {
  const onAction = action?.onClick;
  const actionDisabled = action?.disabled ?? false;
  const id = useId();
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const hasTooltipAction = compact && !!onAction;
  const [align, setAlign] = useState<Align>("center");
  const [vertical, setVertical] = useState<Vertical>("above");
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };
  const show = () => {
    clearTimer();
    const trigger = triggerRef.current;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      setCompact(
        getComputedStyle(trigger)
          .getPropertyValue("--status-pill-compact")
          .trim() === "1"
      );
      setAlign(
        rect.left < 150
          ? "left"
          : window.innerWidth - rect.right < 150
            ? "right"
            : "center"
      );
      setVertical(rect.top < 140 ? "below" : "above");
    }
    setOpen(true);
  };
  const hide = () => {
    clearTimer();
    setLastTooltipCloseTime();
    setOpen(false);
  };
  const focusTrigger = () => {
    triggerRef.current?.querySelector<HTMLElement>("button, a")?.focus();
  };
  const leave = () => {
    if (!hasTooltipAction) {
      hide();
      return;
    }
    clearTimer();
    timerRef.current = setTimeout(() => {
      if (
        !triggerRef.current?.contains(document.activeElement) &&
        !popoverRef.current?.contains(document.activeElement)
      )
        hide();
    }, TOOLTIP_DELAY);
  };
  const blur = (event: React.FocusEvent) => {
    if (
      !triggerRef.current?.contains(event.relatedTarget) &&
      !popoverRef.current?.contains(event.relatedTarget)
    )
      hide();
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const observer = new ResizeObserver(() => {
      if (trigger)
        setCompact(
          getComputedStyle(trigger)
            .getPropertyValue("--status-pill-compact")
            .trim() === "1"
        );
    });
    if (trigger) observer.observe(trigger);
    const outside = (event: PointerEvent) => {
      if (
        !triggerRef.current?.contains(event.target as Node) &&
        !popoverRef.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => {
      observer.disconnect();
      document.removeEventListener("pointerdown", outside);
    };
  }, [open]);

  const pill = (
    <Pill
      id={`${id}-label`}
      style={
        onAction
          ? {
              paddingRight: "calc(3px + 5px * var(--status-pill-compact))",
            }
          : undefined
      }
    >
      <span data-status-label>
        <Text
          variant="text-smaller-md"
          style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {label}
        </Text>
      </span>
      {compactIcon && (
        <span data-compact-icon aria-hidden="true">
          <Icon name={compactIcon} size={10} />
        </span>
      )}
    </Pill>
  );

  const colors = {
    background: `var(--${color}-200)`,
    color: `var(--${color}-600)`,
    "--status-pill-hover-bg": `var(--${color}-400)`,
    "--status-pill-hover-fg": `var(--${color}-800)`,
  } as React.CSSProperties;

  if (passive)
    return (
      <Tooltip content={detail}>
        <Badge
          data-collapse-at={compactAt}
          data-has-compact-icon={!!compactIcon}
          style={colors}
          role="status"
          aria-label={label}
        >
          {pill}
        </Badge>
      </Tooltip>
    );

  return (
    <Container
      onPointerDown={(event) => {
        if (
          !inheritLink ||
          (event.target instanceof Element &&
            event.target.closest("[data-status-action]"))
        )
          event.stopPropagation();
      }}
      onMouseDown={(event) => {
        if (
          !inheritLink ||
          (event.target instanceof Element &&
            event.target.closest("[data-status-action]"))
        )
          event.stopPropagation();
      }}
      onClick={(event) => {
        if (
          !inheritLink ||
          (event.target instanceof Element &&
            event.target.closest("[data-status-action]"))
        ) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.preventDefault();
          event.stopPropagation();
          focusTrigger();
          hide();
        }
      }}
    >
      <Popover
        id={id}
        isOpen={open}
        popoverRef={popoverRef}
        role="group"
        aria-labelledby={`${id}-label`}
        style={{
          ...getPositionStyles(align, vertical),
          padding: hasTooltipAction ? "2px 2px 2px 8px" : undefined,
          pointerEvents: hasTooltipAction ? "auto" : "none",
        }}
        trigger={(props) => (
          <Badge
            ref={triggerRef}
            {...props}
            data-collapse-at={compactAt}
            data-has-compact-icon={!!compactIcon}
            aria-busy={running}
            style={
              {
                ...props.style,
                ...colors,
              } as React.CSSProperties
            }
            onPointerEnter={(event) => {
              if (event.pointerType === "touch") return;
              clearTimer();
              if (Date.now() - getLastTooltipCloseTime() < WARM_PERIOD) show();
              else timerRef.current = setTimeout(show, TOOLTIP_DELAY);
            }}
            onPointerLeave={leave}
            onFocus={show}
            onBlur={blur}
            onKeyDown={(event) => {
              if (
                event.key === "Tab" &&
                !event.shiftKey &&
                open &&
                compact &&
                onAction &&
                !actionDisabled
              ) {
                event.preventDefault();
                popoverRef.current
                  ?.querySelector<HTMLButtonElement>("button")
                  ?.focus();
              }
            }}
          >
            <span className="hover-shape" aria-hidden="true" />
            {inheritLink ? (
              <span
                data-status-trigger
                onPointerDown={hide}
                style={{ display: "inline-flex", minWidth: 0 }}
              >
                {pill}
              </span>
            ) : detailsUrl ? (
              <TriggerLink
                data-status-trigger
                to={detailsUrl}
                aria-label={triggerLabel}
                aria-describedby={open ? id : undefined}
                onPointerDown={hide}
                onClick={(event) => event.stopPropagation()}
              >
                {pill}
              </TriggerLink>
            ) : (
              <Trigger
                data-status-trigger
                onPress={() => {
                  if (onPress) {
                    hide();
                    onPress();
                  } else show();
                }}
                disabled={disabled}
                aria-label={triggerLabel}
                aria-expanded={onPress ? undefined : open}
                aria-haspopup={onPress ? "dialog" : undefined}
                aria-controls={id}
              >
                {pill}
              </Trigger>
            )}
            {onAction && (
              <InlineAction>
                <InlineActionButton
                  data-status-action
                  aria-label={action?.label}
                  aria-disabled={running || actionDisabled}
                  disabled={actionDisabled}
                  onClick={() => {
                    if (!running) onAction();
                  }}
                >
                  <RefreshSpin spinning={running}>
                    <Icon name="refresh-cw" size={10} />
                  </RefreshSpin>
                </InlineActionButton>
              </InlineAction>
            )}
          </Badge>
        )}
      >
        <TooltipBody
          style={
            hasTooltipAction
              ? {
                  padding: vertical === "above" ? "0 8px 8px" : "8px 8px 0",
                  margin: vertical === "above" ? "0 -8px -8px" : "-8px -8px 0",
                }
              : undefined
          }
          onPointerEnter={hasTooltipAction ? clearTimer : undefined}
          onPointerLeave={hasTooltipAction ? leave : undefined}
          onBlur={blur}
          onKeyDown={(event) => {
            if (event.key === "Tab") {
              if (event.shiftKey) event.preventDefault();
              focusTrigger();
              hide();
            }
          }}
        >
          <TooltipMessage>
            <div style={{ minWidth: 0, flex: 1 }}>{detail}</div>
            {compact && onAction && (
              <TooltipActionButton
                data-status-action
                aria-label={action?.label}
                aria-disabled={running || actionDisabled}
                disabled={actionDisabled}
                onClick={() => {
                  if (!running) onAction();
                }}
              >
                <RefreshSpin spinning={running}>
                  <Icon name="refresh-cw" size={12} />
                </RefreshSpin>
              </TooltipActionButton>
            )}
          </TooltipMessage>
        </TooltipBody>
      </Popover>
    </Container>
  );
}
